import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';

function DepartmentManagement() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const departmentsRef = collection(db, 'departments');
      const snapshot = await getDocs(departmentsRef);
      const departmentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDepartments(departmentsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      const newDepartment = {
        name: 'New Department',
        description: '',
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'departments'), newDepartment);
      const departmentWithId = { ...newDepartment, id: docRef.id };
      setDepartments([departmentWithId, ...departments]);
    } catch (error) {
      console.error('Error adding department:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'departments', id));
      setDepartments(departments.filter(department => department.id !== id));
    } catch (error) {
      console.error('Error deleting department:', error);
    }
  };

  const handleStatusToggle = async (departmentId) => {
    try {
      const department = departments.find(t => t.id === departmentId);
      const newStatus = department.status === 'active' ? 'inactive' : 'active';
      
      await updateDoc(doc(db, 'departments', departmentId), { 
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      setDepartments(departments.map(department =>
        department.id === departmentId
          ? { ...department, status: newStatus }
          : department
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleNameChange = async (departmentId, newName) => {
    try {
      await updateDoc(doc(db, 'departments', departmentId), { 
        name: newName,
        updatedAt: serverTimestamp()
      });
      
      setDepartments(departments.map(department =>
        department.id === departmentId
          ? { ...department, name: newName }
          : department
      ));
    } catch (error) {
      console.error('Error updating name:', error);
    }
  };

  const handleDescriptionChange = async (departmentId, newDescription) => {
    try {
      await updateDoc(doc(db, 'departments', departmentId), { 
        description: newDescription,
        updatedAt: serverTimestamp()
      });
      
      setDepartments(departments.map(department =>
        department.id === departmentId
          ? { ...department, description: newDescription }
          : department
      ));
    } catch (error) {
      console.error('Error updating description:', error);
    }
  };

  const handleNameEdit = (departmentId, isEditing) => {
    setDepartments(departments.map(department =>
      department.id === departmentId
        ? { ...department, isEditing }
        : department
    ));
  };

  return (
    <div className="data-container">
      <button className="add-btn" onClick={handleAdd}>
        <FaPlus /> Add Department
      </button>
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {departments.map(department => (
            <tr key={department.id}>
              <td>
                {department.isEditing ? (
                  <input
                    type="text"
                    value={department.name}
                    onChange={(e) => handleNameChange(department.id, e.target.value)}
                    onBlur={() => handleNameEdit(department.id, false)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleNameEdit(department.id, false);
                      }
                    }}
                    autoFocus
                    className="name-input"
                  />
                ) : (
                  <span 
                    onClick={() => handleNameEdit(department.id, true)}
                    className="editable-name"
                  >
                    {department.name}
                  </span>
                )}
              </td>
              <td>
                <input
                  type="text"
                  value={department.description || ''}
                  onChange={(e) => handleDescriptionChange(department.id, e.target.value)}
                  className="description-input"
                  placeholder="Enter description"
                />
              </td>
              <td>
                <button 
                  className={`status-badge ${department.status}`}
                  onClick={() => handleStatusToggle(department.id)}
                >
                  {department.status}
                </button>
              </td>
              <td>
                <button className="delete-button" onClick={() => handleDelete(department.id)}>
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DepartmentManagement;
